// AbDashboard — Startseite der AB-Arbeitsoberflaeche (Vorbild OC-Pilot pages/Dashboard.tsx +
// components/LagerDashboard.js). Hero-Banner, 5 KPI-Kacheln, Posteingang-Panel, 7-Tage-Trend,
// Top-Lieferanten, Kennzahlen heute. GET /dashboard ist fuer alle Modul-Nutzer offen (kein
// requireAdmin in routes.js-Mount) — mailEnabled (fuer den Posteingang-Hinweis) wird nur fuer
// Admins zusaetzlich per mail/status ermittelt (ADMIN-only-Endpoint, s. AbInboxPanel-Kommentar).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Inbox, FileQuestion, ListChecks, AlertOctagon, CheckCircle2, Upload } from 'lucide-react';
import { KpiTile } from './ui';
import AbInboxPanel from './dashboard/AbInboxPanel';
import AbTrendChart from './dashboard/AbTrendChart';
import AbSupplierList from './dashboard/AbSupplierList';
import AbTodayStats from './dashboard/AbTodayStats';
import { getDashboard, mailStatus } from '../api';

const AbDashboard = ({ user, isDarkMode, t, onOpenDocument, onOpenMail, onGoDocuments }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mailEnabled, setMailEnabled] = useState(null); // null = unbekannt (Nicht-Admin oder noch nicht geladen)
  const inboxRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    setLoading(true);
    getDashboard(user)
      .then((res) => { setData(res); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    mailStatus(user).then((s) => setMailEnabled(!!s?.enabled)).catch(() => setMailEnabled(null));
  }, [user, isAdmin]);

  const kpis = data?.kpis || {};
  const openTotal = kpis.openTotal ?? 0;

  const heroCls = isDarkMode
    ? 'bg-gradient-to-r from-indigo-600/60 via-indigo-500/50 to-blue-500/40 border border-white/10'
    : 'bg-gradient-to-r from-indigo-600 to-blue-500';

  return (
    <div className="space-y-6">
      {/* Loading / Error (3-State) */}
      {loading && !data && (
        <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>
      )}
      {error && !data && (
        <div className={`rounded-2xl p-5 text-sm ${isDarkMode ? 'bg-red-500/15 text-red-300 border border-red-500/25' : 'bg-red-50 text-red-600 border border-red-200'}`}>
          {t('ab.shell.dashLoadError')}: {error}
        </div>
      )}

      {data && (
        <>
          {/* Hero */}
          <div className={`relative overflow-hidden rounded-3xl p-6 sm:p-8 flex items-center justify-between gap-6 flex-wrap ${heroCls}`}>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">{t('ab.shell.dashTitle')}</h2>
              <p className="text-sm text-white/80 mt-1">{t('ab.shell.dashSubtitle')}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-4xl font-extrabold text-white tabular-nums">{openTotal > 0 ? openTotal : '✓'}</div>
                <div className="text-xs uppercase tracking-wide text-white/80 font-semibold">
                  {openTotal > 0 ? t('ab.shell.heroOpen', { n: openTotal }) : t('ab.shell.heroClear')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onGoDocuments({ openUpload: true })}
                className="inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-semibold transition-all bg-white text-indigo-700 hover:bg-white/90"
              >
                <span className="flex items-center gap-2"><Upload className="w-4 h-4" />{t('ab.uploadButton')}</span>
              </button>
            </div>
          </div>

          {/* KPI-Kacheln */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiTile isDarkMode={isDarkMode} Icon={Inbox} tone="blue" label={t('ab.shell.kpiInbox')} value={kpis.inboxOpen ?? 0}
              onClick={() => inboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
            <KpiTile isDarkMode={isDarkMode} Icon={FileQuestion} tone="slate" label={t('ab.shell.kpiNoRuleset')} value={kpis.noRuleset ?? 0}
              onClick={() => onGoDocuments({ tab: 'open', status: 'no_ruleset' })} />
            <KpiTile isDarkMode={isDarkMode} Icon={ListChecks} tone="amber" label={t('ab.shell.kpiNeedsReview')} value={kpis.needsReview ?? 0}
              onClick={() => onGoDocuments({ tab: 'open', status: 'needs_review' })} />
            <KpiTile isDarkMode={isDarkMode} Icon={AlertOctagon} tone="red" label={t('ab.shell.kpiFailed')} value={kpis.failed ?? 0}
              onClick={() => onGoDocuments({ tab: 'open', status: 'failed' })} />
            <KpiTile isDarkMode={isDarkMode} Icon={CheckCircle2} tone="green" label={t('ab.shell.kpiTransferredToday')} value={kpis.transferredToday ?? 0}
              onClick={() => onGoDocuments({ tab: 'done', committed: 'true' })} />
          </div>

          {/* Hauptbereich: Posteingang (breit) + Kontext-Spalte */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <AbInboxPanel
                user={user} isDarkMode={isDarkMode} t={t} mailEnabled={mailEnabled}
                onOpenDocument={onOpenDocument} onOpenMail={onOpenMail} panelRef={inboxRef}
              />
            </div>
            <div className="space-y-6">
              <AbTrendChart trend={data.trend} isDarkMode={isDarkMode} t={t} />
              <AbSupplierList suppliers={data.topSuppliers} isDarkMode={isDarkMode} t={t} />
              <AbTodayStats today={data.today} isDarkMode={isDarkMode} t={t} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AbDashboard;
